import { useEffect, useMemo, useState } from 'react'
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { IGuarantorOption } from 'components/modals/guarantorPicker'
import useGuarantorSuggestion from 'pages/loans/manager/addNew/hooks/useGuarantorSuggestion'
import { faPlain, toman } from 'utilities/helper/format'
import WizardChrome, { IReceiptLine } from './components/WizardChrome'
import StickyAction from './components/StickyAction'
import StepBorrower from './steps/StepBorrower'
import StepAmount from './steps/StepAmount'
import StepGuarantor from './steps/StepGuarantor'
import StepTerms, { TermsSummary } from './steps/StepTerms'
import useGrantDraft from './useGrantDraft'
import StepReview from './steps/StepReview'
import useGrantSubmit, { IGrantDraft } from './useGrantSubmit'

const TOTAL_STEPS = 5
const MIN_AMOUNT = 5_000

const STEP_TITLES: Record<number, { title: string; subtitle?: string }> = {
    1: {
        title: 'به چه کسی وام می‌دهید؟',
        subtitle:
            'اعضا بر اساس امتیازشان مرتب شده‌اند — یعنی چقدر پول، چند روز، در صندوق گذاشته‌اند. امتیاز فقط راهنماست؛ تصمیم با شماست.',
    },
    2: { title: 'مبلغ وام چقدر باشد؟' },
    3: {
        title: 'ضامن دارد؟',
        subtitle: 'این مرحله اختیاری است و می‌توانید ردش کنید.',
    },
    4: { title: 'چطور پس بدهد؟' },
    5: { title: 'بازبینی و ثبت' },
}

/**
 * اعطای وام, as a wizard.
 *
 * The screen this replaces was one long form whose first field was «مبلغ
 * وام». That is the second question. The first — «به چه کسی؟» — got a bare
 * name picker and no information at all, which meant the manager of a
 * 500-member fund answered the most consequential question on the screen
 * from memory.
 *
 * Each step asks one thing and then collapses into a line of the receipt at
 * the top, so nothing is hidden and every answer stays one tap away. The
 * step lives in the URL (`?step=`) rather than in state alone so the phone's
 * back button walks the wizard instead of abandoning it — losing four
 * answered questions to a stray back-swipe is the classic way a wizard gets
 * worse than the form it replaced.
 */
export default function GrantLoanWizard() {
    const params = useParams()
    const navigate = useNavigate()
    const { state } = useLocation()
    const [searchParams, setSearchParams] = useSearchParams()

    const moneypoolId = params?.id
    const step = Math.min(
        Math.max(Number(searchParams.get('step') || 1), 1),
        TOTAL_STEPS
    )

    // survives the trip to /manage-bank-cards/add and back
    const {
        borrower,
        setBorrower,
        amount,
        setAmount,
        terms,
        setTerms,
        guarantors,
        setGuarantors,
        note,
        setNote,
        clear: clearDraft,
    } = useGrantDraft(moneypoolId)
    // the card is deliberately NOT persisted: it is owned by useGetCards,
    // which re-reads the account list on the way back — exactly when the
    // manager has just added the account they were missing
    const [card, setCard] = useState<IBankAccountCards | undefined>()

    const suggestion = useGuarantorSuggestion(
        moneypoolId,
        borrower?.poolship_id
    )

    const draft: IGrantDraft = {
        moneypoolId,
        fundName: state?.name,
        borrower,
        amount,
        terms,
        guarantors,
        note,
        card,
    }
    const { submit, isSubmitting } = useGrantSubmit(draft, clearDraft)

    useEffect(() => {
        posthog.capture('loan_grant_step_viewed', {
            fund_id: moneypoolId,
            step,
        })
    }, [step, moneypoolId])

    const goTo = (next: number) => {
        setSearchParams({ step: String(next) })
    }

    // history.back, so the arrow and the phone's own back button agree:
    // every step change pushes an entry, so this walks the wizard until
    // step 1 and then leaves it
    const back = () => navigate(-1)

    const selectBorrower = (candidate: ILoanCandidate) => {
        setBorrower(candidate)
        // a member who filed a request already told us the answer to the next
        // two questions; prefilling is not a decision, it is not retyping
        if (candidate.demand) {
            setAmount(String(candidate.demand.amount))
            setTerms((current) => ({
                ...current,
                installments: String(candidate.demand?.num_repayments ?? ''),
            }))
        }
        goTo(2)
    }

    const receipt = useMemo<IReceiptLine[]>(() => {
        const lines: IReceiptLine[] = []
        if (step > 1 && borrower) {
            lines.push({
                label: 'گیرنده',
                value: borrower.member_name,
                step: 1,
            })
        }
        if (step > 2 && Number(amount) > 0) {
            lines.push({ label: 'مبلغ', value: toman(Number(amount)), step: 2 })
        }
        if (step > 3) {
            lines.push({
                label: 'ضامن',
                value: guarantors.length
                    ? guarantors.map((item) => item.name).join('، ')
                    : 'ندارد',
                step: 3,
            })
        }
        if (step > 4 && Number(terms.installments) > 0) {
            lines.push({
                label: 'اقساط',
                value: `${faPlain.format(Number(terms.installments))} قسط`,
                step: 4,
            })
        }
        return lines
    }, [step, borrower, amount, guarantors, terms])

    const canContinue = () => {
        if (step === 2) return Number(amount) >= MIN_AMOUNT
        if (step === 4) return Number(terms.installments) >= 1
        return true
    }

    const continueLabel = () => {
        if (step === 3 && guarantors.length === 0) return 'بدون ضامن ادامه بده'
        return 'ادامه'
    }

    const onContinue = () => {
        if (!canContinue()) {
            if (step === 2) {
                toast.error('مبلغ وام را وارد کنید')
            } else if (step === 4) {
                toast.error('تعداد اقساط را وارد کنید')
            }
            return
        }
        goTo(step + 1)
    }

    // a deep link to a later step with nothing filled in is a dead end
    useEffect(() => {
        if (step > 1 && !borrower) {
            setSearchParams({ step: '1' }, { replace: true })
        }
    }, [step, borrower, setSearchParams])

    const { title, subtitle } = STEP_TITLES[step]

    return (
        <WizardChrome
            title={title}
            subtitle={subtitle}
            step={step}
            totalSteps={TOTAL_STEPS}
            receipt={receipt}
            onBack={back}
            onJumpTo={goTo}
            tallFooter={step === 5}
        >
            {step === 1 && (
                <StepBorrower
                    moneypoolId={moneypoolId}
                    selectedPoolshipId={borrower?.poolship_id}
                    onSelect={selectBorrower}
                />
            )}
            {step === 2 && (
                <StepAmount
                    moneypoolId={moneypoolId}
                    amount={amount}
                    onChange={setAmount}
                    requestedAmount={borrower?.demand?.amount}
                />
            )}
            {step === 3 && (
                <StepGuarantor
                    moneypoolId={moneypoolId}
                    borrowerPoolshipId={borrower?.poolship_id}
                    borrowerName={borrower?.member_name}
                    suggested={suggestion}
                    value={guarantors}
                    onChange={setGuarantors}
                />
            )}
            {step === 4 && (
                <StepTerms
                    amount={Number(amount || 0)}
                    terms={terms}
                    onChange={setTerms}
                />
            )}
            {step === 5 && (
                <StepReview
                    borrowerName={borrower?.member_name}
                    borrowerPoolshipId={borrower?.poolship_id}
                    borrowerMemberId={borrower?.member_id}
                    fundName={state?.name}
                    amount={Number(amount || 0)}
                    terms={terms}
                    guarantors={guarantors}
                    note={note}
                    onNoteChange={setNote}
                    onCardChange={setCard}
                    selectedCard={card}
                    requestedCardId={
                        borrower?.demand?.selected_card?.id ?? undefined
                    }
                    isSubmitting={isSubmitting}
                    onGrant={() => submit(false)}
                    onRecordOnly={() => submit(true)}
                />
            )}

            {/* step 1 advances by picking somebody and step 5 has its own two
                outcomes, so neither gets a «ادامه» bar */}
            {step > 1 && step < 5 && (
                <StickyAction
                    summary={
                        step === 4 ? (
                            <TermsSummary
                                amount={Number(amount || 0)}
                                terms={terms}
                            />
                        ) : step === 3 && guarantors.length > 0 ? (
                            // a second, unmissable confirmation of who was
                            // picked — the list can be long enough that the
                            // selected row scrolls out of sight
                            <p className=" rounded-xl bg-brand-soft px-3 py-2 text-[11px] font-bold text-brand-deep">
                                ضامن:{' '}
                                {guarantors.map((item) => item.name).join('، ')}
                            </p>
                        ) : undefined
                    }
                >
                    <button
                        type="button"
                        onClick={onContinue}
                        className=" w-full rounded-full bg-green-1 py-3 text-[15px] font-bold text-white transition-transform active:scale-[0.99] motion-reduce:transition-none"
                    >
                        {continueLabel()}
                    </button>
                </StickyAction>
            )}
        </WizardChrome>
    )
}
